import { Card, CardContent, CardHeader, CardTitle } from 'shared/ui/card'
import { Input } from 'shared/ui/input'
import { Textarea } from 'shared/ui/textarea'
import { Label } from 'shared/ui/label'
import { Switch } from 'shared/ui/switch'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'shared/ui/select'
import type { WorkflowMetadata, WorkflowTemplateOption, WorkflowStatusOption } from 'modules/admin/types/workflowTypes'

// Radix Select doesn't allow an empty-string item value, so use a sentinel for "not set" (null).
const FINAL_STATUS_CODE_DEFAULT_VALUE = '__DEFAULT__'

const RESUBMIT_POLICY_OPTIONS = [
  {
    value: 'RESTART' as const,
    label: 'RESTART',
    description: 'Restart workflow from the first stage after resubmission.',
  },
  {
    value: 'RETURNED' as const,
    label: 'RETURNED',
    description: 'Resume only from the stage that returned the permit for revision.',
  },
]

interface WorkflowMetadataPanelProps {
  metadata: WorkflowMetadata
  onChange: (metadata: WorkflowMetadata) => void
  template?: WorkflowTemplateOption | null
  templates: WorkflowTemplateOption[]
  selectedWorkflowVersionId: string | null
  onTemplateChange: (workflowVersionId: string) => void
  statusOptions: WorkflowStatusOption[]
}

export function WorkflowMetadataPanel({
  metadata,
  onChange,
  template,
  templates,
  selectedWorkflowVersionId,
  onTemplateChange,
  statusOptions,
}: WorkflowMetadataPanelProps) {
  const handleChange = (key: keyof WorkflowMetadata, value: string | number | boolean) => {
    onChange({ ...metadata, [key]: value })
  }

  return (
    <Card className="border border-gray-200">
      <CardHeader>
        <CardTitle className="text-base font-semibold text-gray-900">Workflow Profile</CardTitle>
        <p className="text-sm text-gray-500">
          กำหนดข้อมูลพื้นฐานของ workflow รวมถึงรหัสและเวอร์ชัน พร้อมเปิด/ปิดการใช้งาน
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <Label htmlFor="workflowName">Workflow Name</Label>
            <Input
              id="workflowName"
              value={metadata.workflowName}
              onChange={(event) => handleChange('workflowName', event.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="workflowCode">Workflow Code</Label>
            <Input
              id="workflowCode"
              value={metadata.workflowCode}
              onChange={(event) => handleChange('workflowCode', event.target.value.toUpperCase())}
            />
          </div>
          <div>
            <Label htmlFor="workflowVersion">Version</Label>
            <Input
              id="workflowVersion"
              value={metadata.version.toString()}
              onChange={(event) => handleChange('version', Number(event.target.value))}
            />
          </div>
          <div>
            <Label htmlFor="workflowPriority">Priority</Label>
            <Input
              id="workflowPriority"
              type="number"
              value={metadata.priority}
              onChange={(event) => handleChange('priority', Number(event.target.value))}
            />
          </div>
          <div>
            <Label htmlFor="workflowResubmitPolicy">Resubmit Policy</Label>
            <Select
              value={metadata.resubmitPolicy}
              onValueChange={(value) => handleChange('resubmitPolicy', value as WorkflowMetadata['resubmitPolicy'])}
            >
              <SelectTrigger id="workflowResubmitPolicy">
                <SelectValue placeholder="Select resubmit policy" />
              </SelectTrigger>
              <SelectContent>
                {RESUBMIT_POLICY_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="mt-1 text-xs text-gray-500">
              {RESUBMIT_POLICY_OPTIONS.find((option) => option.value === metadata.resubmitPolicy)?.description}
            </p>
          </div>
          <div>
            <Label htmlFor="workflowFinalStatusCode">Final Status Code (เมื่อ Workflow จบ)</Label>
            <Select
              value={metadata.finalStatusCode ?? FINAL_STATUS_CODE_DEFAULT_VALUE}
              onValueChange={(value) =>
                onChange({
                  ...metadata,
                  finalStatusCode: value === FINAL_STATUS_CODE_DEFAULT_VALUE ? null : value,
                })
              }
            >
              <SelectTrigger id="workflowFinalStatusCode">
                <SelectValue placeholder="ใช้ค่าเริ่มต้น (P0080)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={FINAL_STATUS_CODE_DEFAULT_VALUE}>ใช้ค่าเริ่มต้น (P0080 - รอการเปิด Permit)</SelectItem>
                {statusOptions.map((option) => (
                  <SelectItem key={option.statusCode} value={option.statusCode}>
                    {option.statusName} ({option.statusCode})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="mt-1 text-xs text-gray-500">
              สถานะที่ ptw.permits จะอัพเดทเป็น เมื่อ Workflow นี้อนุมัติครบทุกขั้นตอนแล้ว (เช่น งานขุดดิน/งานภาพ ที่ไม่ต้องเปิด Permit ต่อ)
            </p>
          </div>
        </div>
        <div>
          <Label htmlFor="workflowDescription">Description</Label>
          <Textarea
            id="workflowDescription"
            rows={3}
            value={metadata.description ?? ''}
            onChange={(event) => handleChange('description', event.target.value)}
          />
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <Label className="text-sm text-gray-600">Template Version</Label>
            <Select
              value={selectedWorkflowVersionId ?? ''}
              onValueChange={(value) => onTemplateChange(value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select template version" />
              </SelectTrigger>
              <SelectContent>
                {templates.map((option) => (
                  <SelectItem key={option.workflowVersionId} value={option.workflowVersionId}>
                    {option.name} ({option.version})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-sm text-gray-600">Tenant / Site</Label>
            <Input readOnly value={template?.siteName ?? metadata.tenantId ?? ''} />
          </div>
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div>
            <Label className="text-xs uppercase text-gray-500">Tenant Id</Label>
            <Input readOnly value={metadata.tenantId} />
          </div>
          <div>
            <Label className="text-xs uppercase text-gray-500">Template Id</Label>
            <Input readOnly value={metadata.templateId} />
          </div>
          <div>
            <Label className="text-xs uppercase text-gray-500">Template Version Id</Label>
            <Input readOnly value={metadata.templateVersionId} />
          </div>
        </div>
        <div className="flex items-center justify-between rounded-md border border-gray-200 p-3">
          <div>
            <p className="text-sm font-medium text-gray-900">Active Workflow</p>
            <p className="text-xs text-gray-500">เมื่อเปิดใช้งาน ระบบจะเลือก workflow นี้ในลำดับความสำคัญที่ตั้งไว้</p>
          </div>
          <Switch checked={metadata.isActive} onCheckedChange={(checked) => handleChange('isActive', checked)} />
        </div>
      </CardContent>
    </Card>
  )
}
